import { TourConfig, startTour, CONTINUE_PLATFORM_TOUR_BUTTON } from './TourSystem';
import PageWelcomeModal from './PageWelcomeModal';
import 'driver.js/dist/driver.css';

// PHASE 1: Browse Jobs Tab (default view)
const dispatchPhase1Config: TourConfig = {
    tourId: 'dispatch-phase1',
    steps: [
        {
            popover: {
                title: '📡 Welcome to Dispatch',
                description: 'Your command center for finding remote work and posting jobs. This tour will walk you through all three tabs: Browse Jobs, Recommended for You, and My Posted Jobs. Let me show you everything step by step.',
            }
        },
        {
            element: '#dispatch-header',
            popover: {
                title: '🎯 Dispatch Hub Header',
                description: 'This is your main control panel. The header shows you what Dispatch does (find work or post jobs). You can access job posting from anywhere using this header.',
                side: "bottom",
            }
        },
        {
            element: '#dispatch-create-task',
            popover: {
                title: '➕ Post a Job Button',
                description: 'Click this button anytime to post a new job. You can hire talent for any task: design work, coding, marketing, writing, or any other professional service. Set your budget, requirements, and deadlines - then wait for applications.',
                side: "bottom",
            }
        },
        {
            element: '#dispatch-dual-mode',
            popover: {
                title: '⚡ Dual-Core Dispatch',
                description: 'Switch between "Professional Gigs" (high-skill, long-term) and "Community & Instant" (informal, quick labor, no-CV required). This allows you to find exactly the type of work you need.',
                side: "bottom",
            }
        },
        {
            element: '#dispatch-filters',
            popover: {
                title: '📑 Three Navigation Tabs',
                description: 'Dispatch has three main views: (1) Browse Jobs - all available opportunities, (2) Recommended for You - jobs matching your skills, (3) My Posted Jobs - track jobs you have created. Let me show you each one.',
                side: "bottom",
            }
        },
        {
            element: '#dispatch-tab-open',
            popover: {
                title: '🌍 Browse Jobs Tab (Current View)',
                description: 'You are currently viewing all publicly available jobs. These are tasks posted by clients looking for freelancers. Each job card shows: title, description, budget, required skills, deadline, and location (remote/on-site). Click any job to view full details and apply.',
                side: "bottom",
            }
        },
        {
            element: '#dispatch-board',
            popover: {
                title: '📋 Job Listings Board',
                description: 'This area displays job cards based on the active tab. In Browse Jobs mode, you see all opportunities. Each card has an "Apply" button to submit your proposal. The client will review applications and select the best freelancer.',
                side: "top",
            }
        },
        {
            popover: {
                title: '🔄 Switching to Recommended Tab',
                description: 'Now I will show you jobs specifically matched to YOUR skills and profile. This personalized feed increases your chances of getting hired. Switching in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const matchesTab = document.querySelector('#dispatch-tab-matches button');
            if (matchesTab) {
                (matchesTab as HTMLElement).click();
                setTimeout(() => {
                    startDispatchPhase2Tour();
                }, 800);
            }
        }, 1000);
    }
};

// PHASE 2: Recommended Tab
const dispatchPhase2Config: TourConfig = {
    tourId: 'dispatch-phase2',
    steps: [
        {
            element: '#dispatch-tab-matches',
            popover: {
                title: '⭐ Recommended for You Tab',
                description: 'This tab shows jobs that match your profile, skills, and past work history. Our AI algorithm analyzes job requirements and compares them to your abilities. Jobs here have a higher acceptance rate because you are a good fit.',
                side: "bottom",
            }
        },
        {
            element: '#dispatch-board',
            popover: {
                title: '🎯 Smart Match Results',
                description: 'These jobs are pre-filtered for YOU. The matching considers: your listed skills, completed projects, ratings, availability, and price range. Apply to matched jobs for faster responses from clients.',
                side: "top",
            }
        },
        {
            popover: {
                title: '🔄 Switching to My Posted Jobs',
                description: 'Finally, let me show you where to manage jobs YOU have posted as a client. Switching in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const myTasksTab = document.querySelector('#dispatch-tab-mine button');
            if (myTasksTab) {
                (myTasksTab as HTMLElement).click();
                setTimeout(() => {
                    startDispatchPhase3Tour();
                }, 800);
            }
        }, 1000);
    }
};

// PHASE 3: My Posted Jobs Tab
const dispatchPhase3Config: TourConfig = {
    tourId: 'dispatch-phase3',
    steps: [
        {
            element: '#dispatch-tab-mine',
            popover: {
                title: '📝 My Posted Jobs Tab',
                description: 'This tab displays all jobs YOU have posted as a client. Track applications, review proposals, select freelancers, monitor progress, and manage payments. Only jobs you created appear here.',
                side: "bottom",
            }
        },
        {
            element: '#dispatch-board',
            popover: {
                title: '🗂️ Your Job Management Dashboard',
                description: 'Each job card in this view shows: number of applications received, selected freelancer (if any), project status (pending/in-progress/completed), budget vs actual spend, and deadline countdown. Click any job to see applicant proposals.',
                side: "top",
            }
        },
        {
            popover: {
                title: '💼 Complete Workflow',
                description: 'Dispatch works both ways: apply for jobs as a freelancer OR post jobs as a client. The same platform handles both sides of the marketplace. Applications, proposals, and payments all happen in one place.',
            }
        },
        {
            popover: {
                title: '💡 Pro Tips',
                description: '(1) Check "Recommended for You" daily for best matches. (2) Apply quickly to new jobs for competitive advantage. (3) When posting jobs, clear descriptions get better applications. (4) Browse Jobs shows global opportunities - filter by skills to narrow down. (5) Accepted jobs automatically create Project Workspaces.',
            }
        },
        {
            popover: {
                title: '🚀 You Are Ready to Dispatch',
                description: `You now understand all three tabs in Dispatch. Find work in Browse/Recommended, or hire talent using Post Job. Every application and project is tracked end-to-end on TechUnity.${CONTINUE_PLATFORM_TOUR_BUTTON}`,
            }
        }
    ],
};

export const startDispatchTour = () => {
    startTour(dispatchPhase1Config);
};

const startDispatchPhase2Tour = () => {
    startTour(dispatchPhase2Config);
};

const startDispatchPhase3Tour = () => {
    startTour(dispatchPhase3Config);
};

const DispatchTour = () => {
    return (
        <PageWelcomeModal
            title="Welcome to Dispatch"
            description="Your global job board for finding work and hiring talent instantly."
            onStartTour={startDispatchTour}
            tourId="dispatch-phase1"
        />
    );
};

export default DispatchTour;
